import React, { useState } from 'react';
import { ParsedData, CustomTheme } from '../types';
import { extractEntityLabels, ModelEntityLabels } from '../utils/entityUtils';
import LabelEditor from './LabelEditor';

interface EntityReferenceProps {
  data: ParsedData;
  onLabelFilter: (label: string, modelName: string | null) => void;
  activeFilters: Set<string>;
  allowBatchLabelRenaming: boolean;
  onBatchLabelEdit: (oldLabel: string, newLabel: string, newColor: string) => void;
  onSaveTheme?: (theme: Omit<CustomTheme, 'id'>) => void;
  onDeleteLabel?: (label: string, alsoDeleteLabelDefinition: boolean) => void;
  onMergeLabel?: (fromLabel: string, toLabel: string) => void;
  onCreateLabel?: (label: string, color: string) => void;
}

const EntityReference: React.FC<EntityReferenceProps> = ({ 
  data, 
  onLabelFilter, 
  activeFilters,
  allowBatchLabelRenaming,
  onBatchLabelEdit,
  onSaveTheme,
  onDeleteLabel,
  onMergeLabel,
  onCreateLabel
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string>('all');
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  
  const modelEntityLabels = extractEntityLabels(data);
  
  // Get labels to display based on selected model
  const getDisplayLabels = () => {
    if (selectedModel === 'all') {
      // Combine all labels from all models
      const allLabelsMap = new Map<string, { count: number; colorClass: string }>();
      
      modelEntityLabels.forEach(model => {
        model.labels.forEach(label => {
          const existing = allLabelsMap.get(label.label);
          if (existing) {
            existing.count += label.count;
          } else {
            allLabelsMap.set(label.label, {
              count: label.count,
              colorClass: label.colorClass
            });
          }
        });
      });
      
      return Array.from(allLabelsMap.entries())
        .map(([label, info]) => ({
          label,
          count: info.count,
          colorClass: info.colorClass
        }))
        .sort((a, b) => b.count - a.count);
    } else {
      const model = modelEntityLabels.find(m => m.modelName === selectedModel);
      return model?.labels || [];
    }
  };
  
  const displayLabels = getDisplayLabels();
  const totalEntities = displayLabels.reduce((sum, l) => sum + l.count, 0);
  
  const handleLabelClick = (label: string) => {
    const modelFilter = selectedModel === 'all' ? null : selectedModel;
    onLabelFilter(label, modelFilter);
  };
  
  const getFilterKey = (label: string) => {
    return selectedModel === 'all' ? label : `${selectedModel}:${label}`;
  };

  const handleStartEdit = (label: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger filter
    e.preventDefault(); // Prevent default button behavior
    
    // Calculate popup position relative to the button
    const button = e.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    
    // Position popup below and slightly to the left of the button
    // Add scroll offsets for absolute positioning
    setPopupPosition({
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX - 200 // Offset to the left so it's more visible
    });
    
    setEditingLabel(label);
  };

  const handleSaveEdit = (oldLabel: string, newLabel: string, newColor: string) => {
    onBatchLabelEdit(oldLabel, newLabel, newColor);
    setEditingLabel(null);
  };

  const handleCancelEdit = () => {
    setEditingLabel(null);
  };

  const handleDeleteLabel = (label: string, alsoDeleteLabelDefinition: boolean) => {
    if (onDeleteLabel) {
      onDeleteLabel(label, alsoDeleteLabelDefinition);
      setEditingLabel(null); // Close the editor after deletion
    }
  };

  const handleMergeLabel = (fromLabel: string, toLabel: string) => {
    if (onMergeLabel) {
      onMergeLabel(fromLabel, toLabel);
      setEditingLabel(null); // Close the editor after merge
    }
  };

  const handleCreateLabel = () => {
    if (newLabelName.trim() && onCreateLabel) {
      // Check if label already exists
      const exists = displayLabels.some(l => l.label.toLowerCase() === newLabelName.trim().toLowerCase());
      if (exists) {
        alert(`Label "${newLabelName.trim()}" already exists!`);
        return;
      }
      
      // Create with default color
      onCreateLabel(newLabelName.trim(), 'entity-default');
      setNewLabelName('');
      setShowCreateForm(false);
    }
  };
  
  // Get current color for a label (check custom colors first)
  const getCurrentColor = (label: string): string => {
    if (data.customLabelColors && data.customLabelColors[label]) {
      return data.customLabelColors[label];
    }
    // Return the color class from the color utility
    return displayLabels.find(l => l.label === label)?.colorClass || 'entity-default';
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <div className="text-left">
            <h2 className="text-lg font-medium text-gray-900">Entity Label Reference</h2>
            <p className="text-sm text-gray-600">
              {displayLabels.length} unique label{displayLabels.length !== 1 ? 's' : ''}, {totalEntities} total entities
            </p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {/* Content */}
      {isExpanded && (
        <div className="border-t border-gray-200">
          {/* Model Selector - only show if there are models */}
          {data.modelNames && data.modelNames.length > 0 && (
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Model:
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Models</option>
                {modelEntityLabels.map(model => (
                  <option key={model.modelName} value={model.modelName}>
                    {model.modelName} ({model.totalEntities} entities)
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Labels Grid */}
          <div className="p-4">
            {activeFilters.size > 0 && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm font-medium text-blue-900 mb-1">
                  Active Filters ({activeFilters.size})
                </div>
                <div className="text-xs text-blue-700">
                  Click labels below to add/remove filters
                </div>
              </div>
            )}
            
            {/* Create New Label */}
            {onCreateLabel && (
              <div className="mb-4">
                {!showCreateForm ? (
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="w-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg hover:bg-blue-100 hover:border-blue-400 transition-colors"
                  >
                    + Create New Label
                  </button>
                ) : (
                  <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3">
                    <div className="text-xs font-semibold text-green-900 mb-2">Create New Label</div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newLabelName}
                        onChange={(e) => setNewLabelName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleCreateLabel();
                          } else if (e.key === 'Escape') {
                            setShowCreateForm(false);
                            setNewLabelName('');
                          }
                        }}
                        placeholder="Enter label name..."
                        autoFocus
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        onClick={handleCreateLabel}
                        disabled={!newLabelName.trim()}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Create
                      </button>
                      <button
                        onClick={() => {
                          setShowCreateForm(false);
                          setNewLabelName('');
                        }}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      New labels will use a default color. You can customize it after creation.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {displayLabels.map((labelInfo) => {
                const filterKey = getFilterKey(labelInfo.label);
                const isActive = activeFilters.has(filterKey);
                const isEditing = editingLabel === labelInfo.label;
                
                // Check if this label has custom colors
                const colorClass = labelInfo.colorClass;
                const isCustomColor = colorClass.includes('|') || colorClass.startsWith('#') || colorClass.startsWith('rgb');
                
                // Parse custom color format: "bg:#hex|text:#hex|border:#hex"
                let bgColor, textColor, borderColor;
                if (colorClass.includes('|')) {
                  const parts = colorClass.split('|');
                  bgColor = parts[0].replace('bg:', '');
                  textColor = parts[1].replace('text:', '');
                  borderColor = parts[2] ? parts[2].replace('border:', '') : textColor;
                } else if (isCustomColor) {
                  // Legacy single color format
                  bgColor = colorClass + '20';
                  textColor = colorClass;
                  borderColor = colorClass;
                }
                
                return (
                  <div
                    key={labelInfo.label}
                    className={isCustomColor 
                      ? `flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-xs font-medium transition-all border-2 ${
                          isActive ? 'ring-2 ring-blue-500 ring-offset-2 shadow-md' : 'hover:shadow-sm'
                        }`
                      : `flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-xs font-medium transition-all ${labelInfo.colorClass} ${
                          isActive ? 'ring-2 ring-blue-500 ring-offset-2 shadow-md' : 'hover:shadow-sm'
                        }`
                    }
                    style={isCustomColor ? {
                      backgroundColor: bgColor,
                      color: textColor,
                      border: `2px solid ${borderColor}`
                    } : undefined}
                  >
                    {/* Label text - clickable for filtering */}
                    <span
                      onClick={() => handleLabelClick(labelInfo.label)}
                      className="flex-1 text-left truncate hover:opacity-80 transition-opacity cursor-pointer"
                      title={`Click to filter questions with ${labelInfo.label}${selectedModel !== 'all' ? ` in ${selectedModel}` : ''}`}
                    >
                      {labelInfo.label}
                    </span>
                    
                    {/* Edit button and count */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {allowBatchLabelRenaming ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleStartEdit(labelInfo.label, e);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="p-0.5 hover:opacity-70 transition-opacity relative z-10"
                          title="Edit this label (name & color) across all occurrences"
                          style={{ pointerEvents: 'auto' }}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ pointerEvents: 'none' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" style={{ pointerEvents: 'none' }} />
                          </svg>
                        </button>
                      ) : (
                        <div
                          className="p-0.5 opacity-30"
                          title="Batch label editing is disabled. Enable it in settings to edit labels."
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </div>
                      )}
                      <span className="text-xs font-semibold opacity-60">
                        {labelInfo.count}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {displayLabels.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No entity labels found
              </div>
            )}
          </div>
          
          {/* Per-Model Summary */}
          {selectedModel === 'all' && modelEntityLabels.length > 1 && (
            <div className="p-4 bg-gray-50 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Label Count by Model:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {modelEntityLabels.map(model => (
                  <div
                    key={model.modelName}
                    className="p-3 bg-white rounded border border-gray-200"
                  >
                    <div className="font-medium text-gray-900 text-sm mb-1">
                      {model.modelName}
                    </div>
                    <div className="text-xs text-gray-600">
                      {model.labels.length} label types, {model.totalEntities} entities
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Label Editor Popup */}
      {editingLabel && (
        <LabelEditor
          originalLabel={editingLabel}
          currentColor={getCurrentColor(editingLabel)}
          onSave={(newLabel, newColor) => handleSaveEdit(editingLabel, newLabel, newColor)}
          onCancel={handleCancelEdit}
          isOpen={editingLabel !== null}
          position={popupPosition}
          savedThemes={data.savedThemes || []}
          onSaveTheme={onSaveTheme}
          onDelete={handleDeleteLabel}
          onMerge={handleMergeLabel}
          allLabels={displayLabels.map(l => l.label)}
        />
      )}
    </div>
  );
};

export default EntityReference;
